import React, { useState, useEffect, useMemo } from 'react';
import './AdminFAQManager.css';
import { fetchWithAuth, isSuperAdmin, getBranchId } from '../utils/auth';
import { API_URL } from '../config';

const AdminFAQManager = () => {
  const [faqs, setFaqs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [form, setForm]               = useState({ question: '', answer: '', category: 'General', is_visible: true });
  const [addingCategory, setAddingCategory] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [toggling, setToggling]       = useState(null); // id currently being toggled
  const [moving, setMoving]           = useState(null); // id currently being moved
  const [movingCategory, setMovingCategory] = useState(null); // category name currently being moved

  const superAdmin     = isSuperAdmin();
  const defaultBranch  = getBranchId() || 1;
  const [branches, setBranches]               = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(defaultBranch);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => { loadFaqs(); }, [selectedBranchId]);

  // Existing category names for this branch, case-insensitively deduped (first-seen casing wins),
  // sorted alphabetically -- lets the form offer a dropdown of what's already in use instead of
  // admins retyping "Enrollment" / "enrollment" / "enrolment" as separate categories.
  const existingCategories = useMemo(() => {
    const seen = new Map();
    for (const f of faqs) {
      const key = (f.category || 'General').trim().toLowerCase();
      if (!seen.has(key)) seen.set(key, f.category || 'General');
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [faqs]);

  // Rows grouped by category (list is already ordered by category then display_order from the API).
  const groupedFaqs = useMemo(() => {
    const groups = [];
    for (const f of faqs) {
      const last = groups[groups.length - 1];
      if (last && last.category.toLowerCase() === (f.category || 'General').toLowerCase()) {
        last.items.push(f);
      } else {
        groups.push({ category: f.category || 'General', items: [f] });
      }
    }
    return groups;
  }, [faqs]);

  const loadFaqs = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`${API_URL}/api/admin/faq?branch_id=${selectedBranchId}`);
      const data = await res.json();
      if (res.ok) {
        setFaqs(data.faqs || []);
        setError('');
      } else {
        setError(data.error || 'Failed to load FAQs');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ question: '', answer: '', category: existingCategories[0] || 'General', is_visible: true });
    setAddingCategory(existingCategories.length === 0);
    setShowForm(true);
  };

  const openEdit = (faq) => {
    setEditingId(faq.id);
    setForm({ question: faq.question, answer: faq.answer, category: faq.category || 'General', is_visible: faq.is_visible });
    setAddingCategory(false);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setAddingCategory(false);
    setForm({ question: '', answer: '', category: 'General', is_visible: true });
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim() || !form.category.trim()) {
      setError('Question, answer, and category are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const url    = editingId
        ? `${API_URL}/api/admin/faq/${editingId}`
        : `${API_URL}/api/admin/faq`;
      const method = editingId ? 'PUT' : 'POST';
      const body   = editingId
        ? form
        : { ...form, branch_id: selectedBranchId };
      const res  = await fetchWithAuth(url, { method, body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok && data.success) {
        await loadFaqs();
        handleCancel();
      } else {
        setError(data.error || 'Failed to save FAQ.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisible = async (faq) => {
    setToggling(faq.id);
    try {
      const res  = await fetchWithAuth(`${API_URL}/api/admin/faq/${faq.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_visible: !faq.is_visible }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setFaqs(prev => prev.map(f => f.id === faq.id ? data.faq : f));
      } else {
        setError(data.error || 'Failed to update visibility.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setToggling(null);
    }
  };

  const handleMove = async (faq, direction) => {
    setMoving(faq.id);
    try {
      const res  = await fetchWithAuth(`${API_URL}/api/admin/faq/${faq.id}/move`, {
        method: 'PUT',
        body: JSON.stringify({ direction }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await loadFaqs();
      } else {
        setError(data.error || 'Failed to reorder FAQ.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setMoving(null);
    }
  };

  const handleMoveCategory = async (category, direction) => {
    setMovingCategory(category);
    try {
      const res  = await fetchWithAuth(`${API_URL}/api/admin/faq/categories/move`, {
        method: 'PUT',
        body: JSON.stringify({ category, direction, branch_id: selectedBranchId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setFaqs(data.faqs || []);
      } else {
        setError(data.error || 'Failed to reorder category.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setMovingCategory(null);
    }
  };

  const handleDelete = async (id, question) => {
    if (!window.confirm(`Delete FAQ?\n\n"${question.slice(0, 80)}${question.length > 80 ? '…' : ''}"`)) return;
    try {
      const res  = await fetchWithAuth(`${API_URL}/api/admin/faq/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) {
        setFaqs(prev => prev.filter(f => f.id !== id));
      } else {
        setError(data.error || 'Failed to delete FAQ.');
      }
    } catch {
      setError('Network error.');
    }
  };

  return (
    <div className="faq-manager-container">
      {/* Header */}
      <div className="faq-manager-header">
        <div>
          <h2>FAQ Management</h2>
          <p className="faq-manager-subtitle">
            Manage questions and answers shown on the public FAQ page.
          </p>
        </div>
        <div className="faq-manager-header-right">
          {superAdmin && branches.length > 0 && (
            <select
              value={selectedBranchId}
              onChange={e => { setSelectedBranchId(Number(e.target.value)); setShowForm(false); }}
              className="branch-select"
            >
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button className="btn-primary" onClick={openCreate}>+ New FAQ</button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Inline form */}
      {showForm && (
        <div className="form-card faq-form-card">
          <h3 className="faq-form-title">{editingId ? 'Edit FAQ' : 'New FAQ'}</h3>
          <div className="form-row faq-form-row">
            <label>Category</label>
            {addingCategory || existingCategories.length === 0 ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. Enrollment"
                  style={{ flex: 1 }}
                />
                {existingCategories.length > 0 && (
                  <button type="button" className="btn-sm" onClick={() => { setAddingCategory(false); setForm(f => ({ ...f, category: existingCategories[0] })); }}>
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <select
                value={form.category}
                onChange={e => {
                  if (e.target.value === '__new__') {
                    setAddingCategory(true);
                    setForm(f => ({ ...f, category: '' }));
                  } else {
                    setForm(f => ({ ...f, category: e.target.value }));
                  }
                }}
              >
                {existingCategories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__new__">+ Add new category…</option>
              </select>
            )}
          </div>
          <div className="form-row faq-form-row">
            <label>Question</label>
            <textarea
              rows={2}
              value={form.question}
              onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
              placeholder="e.g. What are the class timings?"
            />
          </div>
          <div className="form-row faq-form-row">
            <label>Answer</label>
            <textarea
              rows={4}
              value={form.answer}
              onChange={e => setForm(f => ({ ...f, answer: e.target.value }))}
              placeholder="Enter the full answer here..."
            />
          </div>
          <div className="form-row faq-form-row faq-visible-row">
            <label>Show on public page</label>
            <label className="faq-toggle-label">
              <input
                type="checkbox"
                checked={form.is_visible}
                onChange={e => setForm(f => ({ ...f, is_visible: e.target.checked }))}
              />
              <span className="faq-toggle-track" />
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-secondary" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p style={{ padding: '20px', color: '#64748b' }}>Loading…</p>
      ) : (
        <table className="admin-table faq-table">
          <thead>
            <tr>
              <th style={{ width: '70px' }}>Order</th>
              <th>Question</th>
              <th>Answer</th>
              <th style={{ width: '90px', textAlign: 'center' }}>Public</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {faqs.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px' }}>
                  No FAQs yet. Click "+ New FAQ" to add the first one.
                </td>
              </tr>
            ) : (
              groupedFaqs.map((group, groupIdx) => (
                <React.Fragment key={group.category}>
                  <tr className="faq-category-row">
                    <td colSpan={5}>
                      <button
                        className="btn-sm"
                        onClick={() => handleMoveCategory(group.category, 'up')}
                        disabled={groupIdx === 0 || movingCategory === group.category}
                        title="Move category up"
                      >▲</button>
                      {' '}
                      <button
                        className="btn-sm"
                        onClick={() => handleMoveCategory(group.category, 'down')}
                        disabled={groupIdx === groupedFaqs.length - 1 || movingCategory === group.category}
                        title="Move category down"
                      >▼</button>
                      {' '}
                      {group.category}
                    </td>
                  </tr>
                  {group.items.map((faq, idx) => (
                    <tr key={faq.id} className={!faq.is_visible ? 'faq-row-hidden' : ''}>
                      <td className="faq-order-cell">
                        <button
                          className="btn-sm"
                          onClick={() => handleMove(faq, 'up')}
                          disabled={idx === 0 || moving === faq.id}
                          title="Move up"
                        >▲</button>
                        {' '}
                        <button
                          className="btn-sm"
                          onClick={() => handleMove(faq, 'down')}
                          disabled={idx === group.items.length - 1 || moving === faq.id}
                          title="Move down"
                        >▼</button>
                      </td>
                      <td className="faq-question-cell">{faq.question}</td>
                      <td className="faq-answer-cell">
                        {faq.answer.length > 100 ? faq.answer.slice(0, 100) + '…' : faq.answer}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className={`faq-vis-toggle ${faq.is_visible ? 'vis-on' : 'vis-off'}`}
                          onClick={() => handleToggleVisible(faq)}
                          disabled={toggling === faq.id}
                          title={faq.is_visible ? 'Visible — click to hide' : 'Hidden — click to show'}
                        >
                          {faq.is_visible ? 'Visible' : 'Hidden'}
                        </button>
                      </td>
                      <td>
                        <button className="btn-sm" onClick={() => openEdit(faq)}>Edit</button>
                        {' '}
                        <button className="btn-sm btn-danger" onClick={() => handleDelete(faq.id, faq.question)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminFAQManager;
